'use client'

import { DotPulse } from 'ldrs/react'
import 'ldrs/react/DotPulse.css'
import { useLinkStatus } from 'next/link'
import type { ElementType, PropsWithChildren } from 'react'
import { useEffect, useState } from 'react'

import {
  Button,
  type ButtonProps,
} from '../../@7f-ui/ui-components/src/lib/components/button/button'
import { cx } from '../../@7f-ui/ui-components/src/lib/cx'
import type { ButtonAsProps } from '../../@7f-ui/ui-components/src/lib/polymorphic-props'

type AppLinkLoadingButtonProps<TAs extends ElementType> = PropsWithChildren<ButtonProps> &
  ButtonAsProps<TAs> & {
    pendingDelayMs?: number
  }

export function AppLinkLoadingButton<TAs extends ElementType>({
  children,
  pendingDelayMs = 100,
  ...rest
}: AppLinkLoadingButtonProps<TAs>) {
  return (
    <Button {...rest}>
      <AppLinkLoadingButtonContent pendingDelayMs={pendingDelayMs}>
        {children}
      </AppLinkLoadingButtonContent>
    </Button>
  )
}

type AppLinkLoadingButtonContentProps = PropsWithChildren<{
  pendingDelayMs: number
}>

function AppLinkLoadingButtonContent({
  children,
  pendingDelayMs,
}: AppLinkLoadingButtonContentProps) {
  const { pending } = useLinkStatus()
  const [delayElapsed, setDelayElapsed] = useState(false)
  const [lastPending, setLastPending] = useState(pending)

  if (lastPending !== pending) {
    setLastPending(pending)
    setDelayElapsed(false)
  }

  useEffect(() => {
    if (!pending) return

    const timeoutId = window.setTimeout(() => setDelayElapsed(true), pendingDelayMs)

    return () => window.clearTimeout(timeoutId)
  }, [pending, pendingDelayMs])

  const showPending = pending && delayElapsed

  return (
    <>
      {showPending && (
        <span aria-hidden className="absolute inset-0 z-10 flex items-center justify-center">
          <DotPulse speed={1.3} color="currentColor" />
        </span>
      )}
      <span className={cx({ 'text-transparent': showPending })}>{children}</span>
    </>
  )
}
